import { Dialog, Transition } from "@headlessui/react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import cn from "classnames"
import React, { useState } from "react"
import { type BlockProps } from "."
import { graphql, getFragment } from "@/api"
import { type FrontpagesVideoFragment } from "@/api/graphql"
import { PlayFill } from "@/skylticons"

export const VideoFragment = graphql(`
  fragment FrontpagesVideo on FrontpagesVideo {
    id
    videoUrl
    videoId
    desktopAltText
    mobileAltText
    buttonLabel
    desktop: desktopImage {
      data
      width
      height
      pngDesktop
      pngDesktop2x
      pngTablet
      pngTablet2x
      webpDesktop
      webpDesktop2x
      webpTablet
      webpTablet2x
    }
    mobile: mobileImage {
      data
      width
      height
      pngMobile
      pngMobile2x
      webpMobile
      webpMobile2x
    }
  }
`)

interface VideoImageBlockProps {
  block: FrontpagesVideoFragment
  className: string
  play: (e?: React.MouseEvent) => void
  children: React.ReactNode
}

const VideoImageBlock: React.FC<VideoImageBlockProps> = ({ play, block, className, children }) => {
  return (
    <div className={cn("group container relative", className)}>
      <a href={block.videoUrl} onClick={play} data-gtm="fp-video-play-button" className="cursor-pointer">
        <div className="relative overflow-hidden rounded-2xl">
          <div className="absolute inset-0 z-10 bg-ink-100 opacity-50 transition-opacity group-hover:onlyHover:opacity-60" />
          <div className="absolute inset-0 z-10 flex cursor-pointer items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <PlayFill className="h-12 w-12 text-white sm:h-20 sm:w-20 group-hover:onlyHover:text-white-5" />
              <span className="text-sm font-semibold text-white sm:text-base group-hover:onlyHover:underline">
                {block.buttonLabel}
              </span>
            </div>
          </div>
          {children}
        </div>
      </a>
    </div>
  )
}

const VideoBlock: React.FC<BlockProps<typeof VideoFragment>> = ({ blockable }) => {
  const block = getFragment(VideoFragment, blockable)
  const [open, setOpen] = useState(false)

  const play = (e?: React.MouseEvent) => {
    if (e?.button === 1) {
      return
    }

    e?.preventDefault()
    setOpen(true)
  }

  return (
    <>
      <Transition show={open} as={React.Fragment}>
        <Dialog onClose={() => setOpen(false)} className="relative z-40">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
          </Transition.Child>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Dialog.Panel className="w-[95%] overflow-hidden rounded-2xl lg:w-[75%]">
              <div className="relative h-0 pb-[56.25%]">
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube-nocookie.com/embed/${block.videoId}?autoplay=1`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  className="absolute left-0 top-0 h-full w-full"
                ></iframe>
              </div>
            </Dialog.Panel>
          </div>

          <button
            className="fixed right-4 top-4 flex h-16 w-16 items-center justify-center rounded-full bg-white"
            type="button"
            onClick={() => setOpen(false)}
          >
            <XMarkIcon className="h-8 w-8" />
          </button>
        </Dialog>
      </Transition>

      {block.desktop && (
        <VideoImageBlock play={play} block={block} className="hidden sm:block">
          <picture>
            <source
              srcSet={`${block.desktop.webpDesktop}, ${block.desktop.webpDesktop2x} 2x`}
              media="(min-width: 1280px)"
              type="image/webp"
            />
            <source
              srcSet={`${block.desktop.pngDesktop}, ${block.desktop.pngDesktop2x} 2x`}
              media="(min-width: 1280px)"
            />
            <source srcSet={`${block.desktop.webpTablet}, ${block.desktop.webpTablet2x} 2x`} type="image/webp" />
            <img
              srcSet={`${block.desktop.pngTablet}, ${block.desktop.pngTablet2x} 2x`}
              alt={block.desktopAltText || ""}
              width={block.desktop.width}
              height={block.desktop.height}
              className="h-auto w-full"
              loading="lazy"
            />
          </picture>
        </VideoImageBlock>
      )}

      {block.mobile && (
        <VideoImageBlock play={play} block={block} className="sm:hidden">
          <picture>
            <source srcSet={`${block.mobile.webpMobile}, ${block.mobile.webpMobile2x} 2x`} type="image/webp" />
            <img
              srcSet={`${block.mobile.pngMobile}, ${block.mobile.pngMobile2x} 2x`}
              alt={block.mobileAltText || ""}
              width={block.mobile.width}
              height={block.mobile.height}
              className="h-auto w-full"
              loading="lazy"
            />
          </picture>
        </VideoImageBlock>
      )}
    </>
  )
}

export default VideoBlock
